import { themeMaterial, type Theme } from 'ag-grid-community';
import { RowSpacingType } from '@shared/lib/models/row-spacing-type.enum';

/**
 * PE-13385: central AG Grid theme, replacing the legacy CSS-based
 * `ag-theme-material` styling (see styles/styles.scss history) with AG
 * Grid's Theming API. Built on the `themeMaterial` preset so the existing
 * look-and-feel carries over — this is an infra migration, not a redesign.
 *
 * Density/striping variants below replace the `.ag-theme-material-small`/
 * `-medium`/`-striped` CSS classes that `AgRowSettingsDirective` used to
 * toggle at runtime; it now swaps between these Theme objects instead.
 */

const primaryColor = 'var(--ba-color-primary)';

/** App-wide default — ports the global `.ag-theme-material { ... }` rule (styles.scss). */
export const baseAgGridTheme = themeMaterial.withParams({
  primaryColor,
  accentColor: primaryColor,
  valueChangeValueHighlightBackgroundColor: primaryColor,
  fontFamily: "'DM Sans', sans-serif",
  // themeMaterial's own default row-hover tint is much darker than the
  // legacy CSS file's baked-in default (`#fafafa`) — neither the old CSS
  // nor this migration ever set this explicitly, so it's a preset-default
  // mismatch between the two theme sources, not a param this app tuned.
  rowHoverColor: '#fafafa',
  // Ports the tab bar / column-drop strip background (styles.scss used to
  // read `--ag-subheader-background-color`, which the Theming API never
  // emits — v36 paints this element from `--ag-tab-bar-background-color`
  // instead, computing to transparent if left unset).
  tabBarBackgroundColor: '#eee',
  // ag-grid.css already styled pinned rows with `--ag-pinned-row-font-weight`,
  // which legacy Material never defined (so they rendered at normal weight);
  // the Theming API defaults it to 600, bolding every pinned/grand-total row.
  pinnedRowFontWeight: 'normal',
  pinnedSourceRowFontWeight: 'normal',
});

/** Ports `.ag-theme-material-medium` (styles.scss). */
const mediumDensityParams = {
  spacing: 6,
  iconSize: 17,
  fontSize: 12,
  rowHeight: { calc: 'spacing * 7' },
  headerHeight: { calc: 'spacing * 8' },
  listItemHeight: { calc: 'spacing * 6' },
  rowGroupIndentSize: { calc: 'spacing * 3 + iconSize' },
  cellHorizontalPadding: { calc: 'spacing * 3' },
  inputHeight: { calc: 'spacing * 4' },
};

/** Ports `.ag-theme-material-small` (styles.scss). */
const smallDensityParams = {
  spacing: 5,
  iconSize: 16,
  fontSize: 12,
  rowHeight: { calc: 'spacing * 7' },
  headerHeight: { calc: 'spacing * 8' },
  listItemHeight: { calc: 'spacing * 6' },
  rowGroupIndentSize: { calc: 'spacing * 3 + iconSize' },
  cellHorizontalPadding: { calc: 'spacing * 3' },
  inputHeight: { calc: 'spacing * 4' },
};

/**
 * Density param deltas `AgRowSettingsDirective` layers onto whichever base
 * theme the grid was actually configured with (`large` means no override —
 * the base theme's own values), so a scoped theme like
 * `fileTrackerAgGridTheme` keeps its own tuning instead of being replaced
 * by one of the two named variants below.
 */
export const agGridDensityParams: Record<
  RowSpacingType,
  Record<string, unknown> | null
> = {
  [RowSpacingType.large]: null,
  [RowSpacingType.medium]: mediumDensityParams,
  [RowSpacingType.small]: smallDensityParams,
};

/**
 * Layers a density param delta (from `agGridDensityParams`, or any other
 * `.withParams()`-shaped object) onto an arbitrary theme. `T`'s own param
 * type isn't known here, same as `withGridStriping` below — the cast is
 * safe because every entry in `agGridDensityParams` is itself a valid
 * `themeMaterial` param subset.
 */
export function withDensity<T extends Theme>(
  theme: T,
  params: Record<string, unknown> | null
): T {
  if (!params) {
    return theme;
  }
  return theme.withParams(
    params as unknown as Parameters<T['withParams']>[0]
  ) as T;
}

/**
 * Ports `.ag-theme-material-striped .ag-row-even` (styles.scss) — `mix(black,
 * $lighter-gray #faf9f9, 2%)` precomputed to its resulting color, since Sass
 * `mix()` has no runtime equivalent here. Compose onto whichever
 * density theme is active, mirroring how the old striped class combined with
 * either size class on the same element.
 */
export function withGridStriping<T extends Theme>(theme: T): T {
  return theme.withParams({
    oddRowBackgroundColor: '#f5f4f4',
  } as unknown as Parameters<T['withParams']>[0]) as T;
}

/** Ports `.file-tracker .ag-theme-material` (styles.scss). */
export const fileTrackerAgGridTheme = baseAgGridTheme.withParams({
  spacing: 6,
  iconSize: 16,
  fontSize: 12,
  rowHeight: { calc: 'spacing * 8' },
  headerHeight: { calc: 'spacing * 8' },
  listItemHeight: { calc: 'spacing * 6' },
  rowGroupIndentSize: { calc: 'spacing * 2 + iconSize' },
  cellHorizontalPadding: { calc: 'spacing * 2' },
  inputHeight: { calc: 'spacing * 3' },
});

/** Ports `.client-data-snapshot .ag-theme-material` (styles.scss). */
export const clientDataSnapshotAgGridTheme = baseAgGridTheme.withParams({
  rowGroupIndentSize: 20,
});

/** Ports `.cr-table.ag-theme-material` (native-dashboards.scss — Custom Report). */
export const crTableAgGridTheme = baseAgGridTheme.withParams({
  cellHorizontalPadding: 6,
});

/** Every `ba-native-grid` table (Data Availability and the detail grids). */
export const nativeGridAgGridTheme = baseAgGridTheme.withParams({
  fontSize: 12,
  cellHorizontalPadding: 12,
});

/** Ports `.est-table.ag-theme-material` (native-dashboards.scss — Executive Summary). */
export const estTableAgGridTheme = baseAgGridTheme.withParams({
  cellHorizontalPadding: { calc: 'spacing * 1.5' },
});

/** Ports `.cs-table.ag-theme-material` (native-dashboards.scss — Category Summary). */
export const csTableAgGridTheme = baseAgGridTheme.withParams({
  cellHorizontalPadding: 12,
});

/** Ports `.cqa-table.ag-theme-material` (native-dashboards.scss — Code Quality Assurance). */
export const cqaTableAgGridTheme = baseAgGridTheme.withParams({
  cellHorizontalPadding: 12,
});

/** The tenant offboarding manifest grid: 42px rows, 48px header, 12px type, no outer frame. */
export const tenantOffboardingAgGridTheme = baseAgGridTheme.withParams({
  fontSize: 12,
  headerFontSize: 12,
  headerFontWeight: 500,
  headerTextColor: 'rgba(0, 0, 0, 0.54)',
  headerBackgroundColor: '#fff',
  rowHeight: 42,
  headerHeight: 48,
  cellHorizontalPadding: 18,
  borderColor: '#e2e2e2',
  wrapperBorder: false,
  wrapperBorderRadius: 0,
  selectedRowBackgroundColor: 'transparent',
});
